<template>
  <div v-if="store.state.global.context.selection.organisation">
    <BaseForm
      :title="'Object Authorisation'"
      :data-object="transformedObjectAuthorization"
      :content-type="'object-authorization'"
    >
      <template #content>
        <v-select
          v-if="!manualMode"
          v-model="objectAuthorization.object_type"
          label="Object type"
          color="primary"
          :disabled="props.id !== '0'"
          :items="
            contentTypes.map((contentType) => {
              return {
                value: contentType.name,
                title: contentType.prettyName,
              }
            })
          "
          placeholder="Select a object type"
          @update:model-value="onContentTypeChange"
        />
        <v-text-field
          v-if="manualMode"
          v-model="objectAuthorization.object_type"
          label="Object type"
          @blur="onContentTypeChange(objectAuthorization.object_type)"
          @keyup.enter="onContentTypeChange(objectAuthorization.object_type)"
        />
        <v-text-field
          v-if="manualMode && objectAuthorization.object_id !== undefined"
          v-model="objectAuthorization.object_id"
          label="Object ID"
        />

        <div class="field">
          <v-btn
            v-tooltip:right="`Manual Mode`"
            icon="fa-info-circle"
            variant="text"
          />

          <div class="control">
            <a class="button is-success" @click="switchManualMode()">
              <span class="icon">
                <i v-if="manualMode" class="mdi mdi-pencil-off mdi-24px" />
                <i v-if="!manualMode" class="mdi mdi-pencil mdi-24px" />
              </span>
              <span>{{ manualMode ? 'Enable' : 'Disable' }}</span>
            </a>
          </div>
        </div>
        <v-select
          v-if="roles"
          v-model="objectAuthorization.role"
          label="Role"
          color="primary"
          :placeholder="rolePlaceholder"
          :items="
            roles.objects.map((role) => {
              return {
                value: role.id,
                title: role.name,
              }
            })
          "
          :disabled="roles.objects.length < 1 || props.id !== '0'"
        />
        <v-select
          v-if="groups"
          v-model="objectAuthorization.group"
          label="Group"
          color="primary"
          :items="
            groups.objects.value.map((group) => {
              return {
                value: group.id,
                title: group.name,
              }
            })
          "
          :placeholder="groupPlaceholder"
          :disabled="groups.objects.length < 1 || props.id !== '0'"
        />

        <v-text-field
          v-model.trim="objectAuthorization.note"
          label="Note"
          :loading="loading"
          :disabled="loading"
          placeholder="Note"
          prepend-inner-icon="text-short"
        />

        <transition name="slide-fade">
          <template
            v-if="
              !manualMode &&
              objectAuthorization.object_type !== '' &&
              props.id === '0'
            "
          >
            <div class="card-content">
              <v-btn
                v-if="selected.id !== ''"
                prepend-icon="mdi-window-close"
                :disabled="!Object.keys(selected).length"
                class="button field is-danger"
                @click="selected = { id: '' }"
              >
                Clear selected
              </v-btn>
              <v-data-table-server
                v-model:selected="selected"
                :data="authorizedObjects"
                :loading="collection === null || collection.status.fetching"
              >
                <template #item.name="props">
                  {{ props.row[key] }}
                  <div v-if="relatedModels[objectAuthorization.object_type]">
                    {{
                      relatedModels[objectAuthorization.object_type]
                        .contentType
                    }}:
                    {{
                      getRelatedModel(
                        relatedModels[objectAuthorization.object_type],
                        props.row
                      )
                    }}
                  </div>
                </template>
                <template #item.id="props"
                  ><code>{{ props.row.id }}</code></template
                >

                <template #no-data>
                  <section class="section">
                    <div class="content has-text-grey has-text-centered">
                      <p>
                        <v-icon icon="cancel" size="larger" />
                      </p>
                      <p>Nothing here.</p>
                    </div>
                  </section>
                </template>
              </v-data-table-server>
            </div>
          </template>
        </transition>
      </template>
    </BaseForm>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, Ref, ref } from 'vue'
import { useStore } from 'vuex'

import { apiClient, CollectionSubscriber } from '@/api/ApiClient'
import BaseForm from '@/components/common/BaseForm.vue'
import { contentTypes } from '@/models/core/defaults'
import { Group } from '@/models/core/models'
import { ObjectAuthorization } from '@/models/core/models'

type ObjectAuthorizationFormProps = {
  id: string
}
const store = useStore()
const props = defineProps<ObjectAuthorizationFormProps>()

const objectAuthorization: Ref<ObjectAuthorization> = ref(null)
const collection = ref(null)
const groups = Group.collection(
  {
    organisation: store.getters['global/organisation'].id,
  },
  {
    page: 1,
    pageSize: 30,
  }
)
const manualMode = ref(false)
const roles = store.state.global.context.roles

const loading = ref(false)
const selected = ref({ id: '' })
const key = ref('')
const relatedModelInfos = ref({})
const relatedModels = {}

onMounted(() => {
  collection.value = apiClient.subscribeList('organisation')
  loading.value = true
  apiClient.get('object-authorization', props.id).then((response) => {
    objectAuthorization.value = response
    loading.value = false
  })
})

onUnmounted(() => {
  collection.value.dispose()
  collection.value = null
})

function switchManualMode() {
  if (manualMode.value) {
    if (objectAuthorization.value.object_type) {
      // Check if its a known content type, otherwise dispose collection before switching back
      let knownType = false
      contentTypes.forEach((contentType) => {
        if (contentType.name === objectAuthorization.value.object_type) {
          knownType = true
        }
      })
      if (!knownType) {
        objectAuthorization.value.object_type = ''
        onContentTypeChange(null)
      }
    }
  }
  manualMode.value = !manualMode.value
}

function onContentTypeChange(value) {
  loading.value = true
  contentTypes.forEach((contentType) => {
    if (contentType.name === value) {
      collection.value.dispose()
      // TODO: This should be declared in the contentType probably, or on model..
      if (contentType.name === 'client.clientappsettingkind') {
        collection.value = apiClient.subscribeList(contentType.apiName, {
          client_app: store.getters['global/selectedClientApp'].id,
        })
      } else if (contentType.name === 'brelag/project') {
        collection.value = apiClient.subscribeList(
          contentType.apiName,
          {
            organisation: store.getters['global/organisation'].id,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      } else {
        collection.value = apiClient.subscribeList(contentType.apiName, {
          organisation: store.getters['global/organisation'].id,
        })
      }
      key.value = contentType.key
      selected.value = { id: '' }
    }
  })
  loading.value = false
}

const transformedObjectAuthorization = computed(() => {
  if (props.id === '0') {
    if (!objectAuthorization.value) objectAuthorization.value = {}
    objectAuthorization.value.object_id = selected.value.id
    objectAuthorization.value.organisation =
      store.state.global.context.selection.organisation.id
  }
  return objectAuthorization.value
})

const rolePlaceholder = computed(() => {
  if (roles.value && roles.value.objects.length < 1) {
    return 'No roles available.'
  } else {
    return 'Select role.'
  }
})

const groupPlaceholder = computed(() => {
  if (groups.value && groups.value.objects.length < 1) {
    return 'No groups available.'
  } else {
    return 'Select group.'
  }
})

const authorizedObjects = computed(() => {
  return collection.value.objects.filter((obj) => {
    return obj._permissions.authorize
  })
})

function getRelatedModel(relatedModel: any, row: any): string {
  if (relatedModelInfos.value[row.id]) {
    return relatedModelInfos.value[row.id]
  } else {
    apiClient
      .get(relatedModel.contentType, row[relatedModel.contentType])
      .then((response) => {
        relatedModelInfos.value[row.id] = response[relatedModel.key]
      })
    return 'Loading...'
  }
}
</script>
